import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Autocomplete,
  Divider,
  MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "../../contexts/snackbar-context";
import StatusFilter from "./status-filter";
import { useTailNumbers } from "../../hooks/useTailNumbers";
import { useWarehouseCategories } from "../../hooks/useWarehouseCategories";
import { useSubAssemblyPoolFolders } from "../../hooks/useSubAssemblyPoolFolders";
import { useNasRootFolders } from "../../hooks/useNasRootFolders";
import { useHasPermission } from "../../hooks/useHasPermission";

// Common style constants
const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
};

const commonInputStyles = {
  "& .MuiInputLabel-root": {
    color: `${COLORS.white} !important`,
  },
  "& .MuiOutlinedInput-root": {
    color: COLORS.white,
    "& fieldset": {
      borderColor: `${COLORS.white} !important`,
    },
    "&:hover fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
  },
  "& .MuiSvgIcon-root": {
    color: `${COLORS.white} !important`,
  },
};

type DocumentSearchScope = "airframe" | "assembly" | "subAssembly" | "nas";

interface SearchFilters {
  documentScope: DocumentSearchScope;
  assemblyType: string;
  assemblyFolder: string;
  assemblyComponentId?: string;
  aircraft: string;
  nasRootFolder: string;
  documentTitle: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  statuses: string[];
  withoutRegistration?: boolean;
}

type SearchFormState = Omit<SearchFilters, "documentScope"> & {
  documentScope: "" | DocumentSearchScope;
};

interface SearchPanelProps {
  onSearch: (filters: SearchFilters) => void;
  onStatusChange?: (statuses: string[]) => void;
  onClearResults?: () => void;
}

const defaultStatuses = ["completed", "inProgress", "pending"];

const createDefaultFilters = (
  scope: "" | DocumentSearchScope,
  withoutRegistration = false
): SearchFormState => ({
  documentScope: scope,
  assemblyType: scope === "subAssembly" ? "landingGear" : "",
  assemblyFolder: "",
  assemblyComponentId: "",
  aircraft: "",
  nasRootFolder: "",
  documentTitle: "",
  description: "",
  startDate: null,
  endDate: null,
  statuses: [...defaultStatuses],
  withoutRegistration: scope === "airframe" ? withoutRegistration : false,
});

const SearchPanel: React.FC<SearchPanelProps> = ({
  onSearch,
  onStatusChange,
  onClearResults,
}) => {
  const canSearchIata = useHasPermission("documents-search", "read");
  const canBrowseNas = useHasPermission("documents-nas", "read");

  const [filters, setFilters] = useState<SearchFormState>({
    ...createDefaultFilters("", false),
  });
  const [isGroupedMode, setIsGroupedMode] = React.useState<boolean>(false);

  const [showAircraftError, setShowAircraftError] = React.useState(false);
  const { tailNumbers, loading } = useTailNumbers();
  const { getTopLevelFolders } = useWarehouseCategories(filters.assemblyType);
  const { folders: subAssemblyPoolFolders } = useSubAssemblyPoolFolders(filters.documentScope === "subAssembly");
  const { roots: nasRootFolders, loading: nasRootsLoading } = useNasRootFolders(
    filters.documentScope === "nas"
  );
  const { showError } = useSnackbar();

  React.useEffect(() => {
    if (canSearchIata || !canBrowseNas) {
      return;
    }

    setFilters((prev) =>
      prev.documentScope === "nas" ? prev : createDefaultFilters("nas")
    );
  }, [canSearchIata, canBrowseNas]);

  const handleFilterChange = (field: keyof SearchFormState, value: any) => {
    // If aircraft is changed, reset all filters including statuses
    if (field === "aircraft") {
      setFilters({
        ...createDefaultFilters(filters.documentScope, false),
        aircraft: value || "",
      });
      setShowAircraftError(false);

      if (onStatusChange) {
        onStatusChange(defaultStatuses);
      }
      return;
    }

    // If assembly type changed, reset folder
    if (field === "assemblyType") {
      setFilters((prev) => ({
        ...prev,
        assemblyType: value || "",
        assemblyFolder: "",
        assemblyComponentId: "",
      }));
      return;
    }

    if (field === "assemblyFolder") {
      const selectedFolder = getTopLevelFolders().find(
        (category) => category.code === (value || "")
      );

      setFilters((prev) => ({
        ...prev,
        assemblyFolder: value || "",
        assemblyComponentId: selectedFolder?.componentId ?? "",
      }));
      return;
    }

    // If statuses changed, ensure at least one is selected
    if (field === "statuses" && Array.isArray(value)) {
      if (value.length === 0) {
        showError("At least one status must be selected");
        return;
      }
    }

    setFilters((prev: SearchFormState) => ({ ...prev, [field]: value }));

    if (field === "statuses" && onStatusChange && Array.isArray(value)) {
      onStatusChange(value);
    }
  };

  const handleDocumentScopeChange = (
    scope: "" | DocumentSearchScope
  ) => {
    if (onClearResults) {
      onClearResults();
    }

    setIsGroupedMode(false);
    setShowAircraftError(false);
    setFilters(createDefaultFilters(scope, false));

    if (onStatusChange) {
      onStatusChange(defaultStatuses);
    }
  };

  const handleSearch = () => {
    if (!filters.documentScope) {
      showError("Document Search Type is required");
      return;
    }

    if (filters.documentScope === "assembly") {
      if (!filters.assemblyType) {
        showError("Assembly Type is required");
        return;
      }
      if (!filters.assemblyFolder) {
        showError("Assembly is required");
        return;
      }
    }

    if (filters.documentScope === "subAssembly") {
      if (!filters.assemblyFolder) {
        showError("Assembly is required");
        return;
      }
    }

    if (filters.documentScope === "nas") {
      if (!filters.nasRootFolder || filters.nasRootFolder.trim() === "") {
        showError("NAS folder is required");
        return;
      }

      setShowAircraftError(false);
      onSearch({
        ...filters,
        documentScope: "nas",
      });
      return;
    }

    // Validate aircraft field is required in normal mode
    if (
      filters.documentScope === "airframe" &&
      !filters.withoutRegistration &&
      (!filters.aircraft || filters.aircraft.trim() === "")
    ) {
      setShowAircraftError(true);
      showError("Aircraft Registration Number is required");
      return;
    }

    setShowAircraftError(false);
    onSearch({
      ...filters,
      documentScope: filters.documentScope as DocumentSearchScope,
    });
    // Clear deep-search keyword after submit so the field is empty for the next query
    setFilters((prev) => ({
      ...prev,
      description: "",
    }));
  };

  const handleSwitchToGroupedMode = () => {
    if (filters.documentScope !== "airframe") {
      return;
    }

    if (onClearResults) {
      onClearResults();
    }

    // Switch to grouped search mode (without registration)
    setIsGroupedMode(true);
    setFilters(createDefaultFilters("airframe", true));

    if (onStatusChange) {
      onStatusChange(defaultStatuses);
    }
  };

  const handleSwitchToNormalMode = () => {
    if (onClearResults) {
      onClearResults();
    }
    setIsGroupedMode(false);
    setFilters(createDefaultFilters("airframe", false));
    if (onStatusChange) {
      onStatusChange(defaultStatuses);
    }
  };

  const handleClear = () => {
    setFilters(createDefaultFilters(filters.documentScope, isGroupedMode));
    setShowAircraftError(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper
        sx={{
          height: "80vh",
          border: "1px solid white",
          background: "none",
          p: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: "hsl(19 101% 51% / 1)", flexShrink: 0 }}
        >
          Search Filters
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            pt: 1,
          }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Document Search Type"
            value={filters.documentScope}
            onChange={(e) =>
              handleDocumentScopeChange(
                e.target.value as "" | DocumentSearchScope
              )
            }
            SelectProps={{
              sx: {
                "& .MuiSelect-select": {
                  whiteSpace: "normal",
                  overflow: "visible",
                  textOverflow: "clip",
                  lineHeight: 1.4,
                },
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      whiteSpace: "normal",
                    },
                  },
                },
              },
            }}
            sx={{
              ...commonInputStyles,
              flexShrink: 0,
              "& .MuiInputLabel-root": {
                ...commonInputStyles["& .MuiInputLabel-root"],
                whiteSpace: "nowrap",
                overflow: "visible",
              },
            }}
          >
            <MenuItem value="" disabled>
            </MenuItem>
            {canSearchIata && <MenuItem value="airframe">Airframes</MenuItem>}
            {canSearchIata && !isGroupedMode && <MenuItem value="assembly">OffWing Assemblies</MenuItem>}
            {canSearchIata && !isGroupedMode && <MenuItem value="subAssembly">OffWing Sub-Assemblies</MenuItem>}
            {!isGroupedMode && canBrowseNas && <MenuItem value="nas">NAS Docs</MenuItem>}
          </TextField>

          {filters.documentScope !== "" && (
            <>
              {!isGroupedMode && filters.documentScope === "airframe" && (
            <Autocomplete
              size="small"
              options={tailNumbers}
              loading={loading}
              value={filters.aircraft || null}
              onChange={(event, newValue) =>
                handleFilterChange("aircraft", newValue || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aircraft Registration No."
                  fullWidth
                  required
                  error={showAircraftError}
                  sx={{
                    ...commonInputStyles,
                    "& .MuiAutocomplete-popupIndicator": {
                      color: `${COLORS.white} !important`,
                    },
                    "& .MuiAutocomplete-clearIndicator": {
                      color: `${COLORS.white} !important`,
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  ...commonInputStyles["& .MuiOutlinedInput-root"],
                  "&.Mui-error fieldset": {
                    borderColor: "#f44336 !important",
                  },
                },
              }}
            />
              )}
              {filters.documentScope === "assembly" && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Assembly Type"
                value={filters.assemblyType}
                onChange={(e) =>
                  handleFilterChange("assemblyType", e.target.value)
                }
                sx={commonInputStyles}
                required
              >
                <MenuItem value="engine">Engine</MenuItem>
                <MenuItem value="apu">APU</MenuItem>
                <MenuItem value="landingGear">Landing Gear</MenuItem>
              </TextField>

              {filters.assemblyType && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Assembly"
                  value={filters.assemblyFolder}
                  onChange={(e) =>
                    handleFilterChange("assemblyFolder", e.target.value)
                  }
                  sx={commonInputStyles}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 320 },
                      },
                    },
                  }}
                  required
                >
                  {getTopLevelFolders().map((folder) => (
                    <MenuItem key={folder.code} value={folder.code}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
              )}

              {filters.documentScope === "subAssembly" && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Assembly Type"
                value="landingGear"
                InputProps={{ readOnly: true }}
                sx={commonInputStyles}
              >
                <MenuItem value="landingGear">Landing Gear</MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Assembly"
                value={filters.assemblyFolder}
                onChange={(e) =>
                  handleFilterChange("assemblyFolder", e.target.value)
                }
                sx={commonInputStyles}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 320 },
                    },
                  },
                }}
                required
              >
                {subAssemblyPoolFolders.map((folder) => (
                  <MenuItem key={folder.code} value={folder.code}>
                    {folder.name}
                  </MenuItem>
                ))}
              </TextField>
            </>
              )}
              {filters.documentScope === "nas" && (
                <Autocomplete
                  size="small"
                  options={nasRootFolders}
                  loading={nasRootsLoading}
                  value={filters.nasRootFolder || null}
                  onChange={(event, newValue) =>
                    handleFilterChange("nasRootFolder", newValue || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="NAS Folder"
                      fullWidth
                      required
                      sx={{
                        ...commonInputStyles,
                        "& .MuiAutocomplete-popupIndicator": {
                          color: `${COLORS.white} !important`,
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: `${COLORS.white} !important`,
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      ...commonInputStyles["& .MuiOutlinedInput-root"],
                    },
                  }}
                />
              )}
              {filters.documentScope !== "nas" && (
                <>
              <TextField
            fullWidth
            size="small"
            label="Form No."
            value={filters.documentTitle}
            onChange={(e) =>
              handleFilterChange("documentTitle", e.target.value)
            }
            sx={commonInputStyles}
          />
              {filters.documentScope === "airframe" && (
              <StatusFilter
            selectedStatuses={filters.statuses}
            onChange={(statuses) => handleFilterChange("statuses", statuses)}
            showIndexingStatus
          />
              )}
              {filters.documentScope === "airframe" && (
          <Paper
            sx={{
              p: 2,
              border: "1px solid rgba(255, 255, 255, 0.3)",
              background: "rgba(255, 255, 255, 0.05)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: COLORS.white,
                fontStyle: "italic",
              }}
            >
              Add keyword  below, to enable Deep Document search and Image Cracking using OCR. Or ask semantic questions like "Find me the document with signature".
            </Typography>
            <TextField
            fullWidth
            size="small"
            label="KeyWord  for Deep Search"
            multiline
            rows={2}
            value={filters.description}
            onChange={(e) => handleFilterChange("description", e.target.value)}
            sx={commonInputStyles}
          />
          </Paper>
              )}
                </>
              )}
              {filters.documentScope === "airframe" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
                <Divider sx={{ flex: 1, borderColor: "rgba(255,255,255,0.3)" }} />
                <Typography sx={{ mx: 2, color: COLORS.white, fontSize: "0.9rem" }}>
                  OR
                </Typography>
                <Divider sx={{ flex: 1, borderColor: "rgba(255,255,255,0.3)" }} />
              </Box>
              <Button
                variant="contained"
                onClick={
                  isGroupedMode
                    ? handleSwitchToNormalMode
                    : handleSwitchToGroupedMode
                }
                startIcon={<SearchIcon />}
                fullWidth
                sx={{
                  color: COLORS.white,
                  backgroundColor: COLORS.primary,
                  "&:hover": { backgroundColor: COLORS.primaryHover },
                }}
              >
                {isGroupedMode
                  ? "Search With Registration Number"
                  : "Search Without Registration Number"}
              </Button>
            </>
              )}
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2, pt: 2, mt: "auto", flexShrink: 0 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{
              flex: 1,
              color: COLORS.white,
              backgroundColor: COLORS.primary,
              "&:hover": { backgroundColor: COLORS.primaryHover },
            }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              flex: 1,
              color: COLORS.white,
              borderColor: COLORS.white,
              "&:hover": {
                borderColor: COLORS.white,
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default SearchPanel;
